import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowsUpDownIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { Tooltip as ReactTooltip } from "react-tooltip";
import * as XLSX from "xlsx";
import Modal from "./modals/reasoncode";
import {
  deleteReasonCode,
  getReasonCodeDetails,
  uploadreasoncodexl,
} from "~/services/supervisor.services";

import DownloadModal from "./modals/download";
import { toast } from "react-toastify";
interface IReasonCodeTableProps {
  data: any[];
  onDataUpdate: (data: any[]) => void;
}
// Define a type for selected row IDs
type SelectedRowIds = Record<string, boolean>;
const ReasonCodeTable = ({ data, onDataUpdate }: IReasonCodeTableProps) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });
  const [selectedRowIds, setSelectedRowIds] = useState<SelectedRowIds>({});
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [EditedUser, setEditedUser] = useState<any>(null);
  const [filteredData, setFilteredData] = useState<any[]>(data);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isDownloadModalOpen, setIsDownloadModalOpen] =
    useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSuccessModalReload, setShowSuccessModalReload] = useState(false);
  const [SuccessMessage, setSuccessMessage] = useState("");
  const handleOpenDownloadModal = () => {
    setIsDownloadModalOpen(true);
  };

  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({
    reasoncode: "",
    description: "",
    reasontypename: "",
    rolename: "",
    errorcategory: "",
    errorsubcategory: "",
    doctypes: "",
  });

  // Debounce function for search input
  // eslint-disable-next-line @typescript-eslint/ban-types
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(
    debounce((key: string, value: string) => {
      setSearchInputs((prev) => ({
        ...prev,
        [key]: value,
      }));
    }, 0), // Adjusted delay for better user experience
    []
  );

  const handleInputChange = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      debouncedSetSearch(key, value);
    },
    [debouncedSetSearch]
  );

  // Filtered data based on search inputs
  useEffect(() => {
    const filtered = data.filter((row) =>
      Object.entries(searchInputs).every(
        ([key, searchTerm]) =>
          !searchTerm ||
          row[key]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredData(filtered);
    setCurrentPage(0); // Reset to first page on search change
  }, [searchInputs, data]);

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columnHeaders: Record<string, string> = {
    reasoncode: "Reason Code",
    description: "Description",
    reasontypename: "Reason Type",
    rolename: "Role",
    errorcategory: "Error Category",
    errorsubcategory: "Error Sub Catagory",
    doctypes: "Document Type",
  };
  // const tableData = useMemo(() => data, [data]);
  const columnHelper = createColumnHelper<any>();

  const requestSort = (key: string) => {
    setSortConfig((prevConfig) => {
      let direction: "asc" | "desc" = "asc";

      if (prevConfig.key === key) {
        // Toggle the direction if the same key is clicked
        direction = prevConfig.direction === "asc" ? "desc" : "asc";
      }

      return { key, direction };
    });
  };

  const DeleteAll = async () => {
    const idsToDelete = Object.keys(selectedRowIds);
    const intKeys = idsToDelete.map((key) => parseInt(key, 10));
    // Check if idsToDelete is empty
    if (idsToDelete.length === 0) {
      toast.error("No rows selected for deletion.", {
        theme: "colored",
      });
      return; // Exit the function early
    }
    // Perform deletions and wait for all to complete
    const deletePromises = idsToDelete.map((key) => deleteReasonCode(key));
    const responses = await Promise.all(deletePromises);

    const allSuccessful = responses.every((response) => response.status);

    if (allSuccessful) {
      const updatedData = data.filter(
        (reasoncode) => !intKeys.includes(reasoncode.id)
      );
      ``;
      onDataUpdate(updatedData);

      // Clear row selection
      tableObj.setRowSelection({});

      setSuccessMessage("Record(S) deleted successfully");
      setShowSuccessModalReload(true);
      setTimeout(() => {
        setShowSuccessModalReload(false);
      }, 3000);
      console.log("Rows deleted with IDs:", idsToDelete.join(", "));
    } else {
      console.log("Some deletions failed.");
      toast.error("Some deletions failed.", {
        theme: "colored",
      });
    }

    setSelectedRowIds({});

    console.log("Deletion process completed for selected rows.");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteReasonCode(id);

      // Check if the response indicates success
      if (response.status) {
        handleSuccess(id);
      } else {
        handleFailure();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleSuccess = (id: string) => {
    console.log("Reason code deleted");
    setSuccessMessage("Record deleted successfully");
    setShowSuccessModalReload(true);

    setTimeout(() => {
      setShowSuccessModalReload(false);
    }, 3000);

    const updatedData = data.filter((reasoncode) => reasoncode.id !== id);
    onDataUpdate(updatedData); // Notify parent component of the data change
  };

  // Handle failure scenario
  const handleFailure = () => {
    console.error("Failed to delete reason code");
  };

  // Handle errors
  const handleError = (error: any) => {
    console.error("Error deleting reason code", error);
  };
  const handleEditClick = async (ReasonCodeId: number) => {
    setEditedUser(ReasonCodeId);
    setIsEditModalOpen(true);
  };
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a: any, b: any) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, rowsPerPage]);

  const handleExportToExcel = () => {
    const keys = Object.keys(selectedRowIds);
    const intKeys = keys.map((key) => parseInt(key, 10));
    const selectedRows = sortedData.filter((row) => intKeys.includes(row.id));

    // Use includes to check for keys
    console.log("Selected Rows:", selectedRows);

    if (selectedRows.length <= 0) {
      toast.error("No rows selected for export.", {
        theme: "colored",
      });
      return false;
    }
    // Map the selected rows to the desired structure
    const formattedRows = selectedRows.map((row: any) => ({
      "Reason Code": row.reasoncode,
      Description: row.description,
      ReasonType: row.reasontypename,
      Role: row.rolename,
      "Error Category": row.errorcategory,
      "Error Sub-Category": row.errorsubcategory,
      "Document Types": row.doctypes,
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedRows);

    // Calculate column widths based on headers and cell values
    const headers = Object.keys(formattedRows[0]);
    const columnWidths: number[] = headers.map((header) => {
      const headerLength = header.length;
      const maxCellLength = formattedRows.reduce(
        (max, row: Record<string, any>) => {
          const cellValue = row[header] ? row[header].toString() : "";
          return Math.max(max, cellValue.length);
        },
        0
      );
      return Math.max(headerLength, maxCellLength);
    });

    // Add a little extra space to each column width
    const widthSettings = columnWidths.map((width) => ({ wch: width + 5 }));

    worksheet["!cols"] = widthSettings;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ReasonCodes");

    XLSX.writeFile(workbook, "ReasonCodes.xlsx");
    setSelectedRowIds({});
  };

  const columns = useMemo(() => {
    const createHeader = (key: string) => {
      // Get the display name from the mapping or use the key itself as fallback
      const headerName = columnHeaders[key] || key;

      return (
        <div className="p-2">
          <div className="flex flex-col gap-2 justify-center">
            <div className="flex gap-2 justify-center">{headerName}</div>
            <div className="flex items-center justify-center gap-2">
              <input
                type="text"
                value={searchInputs[key] || ""}
                onChange={handleInputChange(key)}
                // placeholder={`Search ${headerName}`}
                className="border border-gray-300 rounded"
              />
              <button onClick={() => requestSort(key)}>
                <ArrowsUpDownIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      );
    };

    const headerEdit = <div className="p-2">Edit</div>;
    const headerDelete = <div className="p-2">Delete</div>;
    const headerSelect = (
      <input
        type="checkbox"
        checked={Object.keys(selectedRowIds).length === filteredData.length} // Select all if all are selected
        onChange={() => {
          console;
          const allSelected =
            Object.keys(selectedRowIds).length === filteredData.length;
          const newSelectedIds: SelectedRowIds = {};
          if (!allSelected) {
            filteredData.forEach((row) => {
              newSelectedIds[row.id] = true; // Assuming row.id is unique
            });
          }
          setSelectedRowIds(newSelectedIds);
        }}
      />
    );

    return [
      {
        id: "select",
        header: headerSelect,
        // eslint-disable-nex t-line @typescript-eslint/no-unused-vars
        cell: (props: any) => (
          <input
            type="checkbox"
            checked={selectedRowIds[props.row.original.id] || false}
            onChange={() => {
              setSelectedRowIds((prev) => {
                const newSelectedRowIds = { ...prev };
                if (newSelectedRowIds[props.row.original.id]) {
                  delete newSelectedRowIds[props.row.original.id];
                } else {
                  newSelectedRowIds[props.row.original.id] = true;
                }
                return newSelectedRowIds;
              });
            }}
          />
        ),
      },
      {
        id: "edit",
        header: headerEdit,
        cell: (props: any) => (
          <div
            className="flex items-center justify-center gap-2 cursor-pointer"
            onClick={() => handleEditClick(props.row.original.id)}
            data-tooltip-content="Edit"
            data-tooltip-id="EditTooltip"
          >
            <ReactTooltip id="EditTooltip" />
            <PencilIcon className="h-4 w-4 text-blue-500" />
          </div>
        ),
      },
      {
        id: "delete",
        header: headerDelete,
        cell: (props: any) => (
          <div
            className="flex items-center justify-center gap-2 cursor-pointer"
            onClick={async () => {
              const confirmDelete = window.confirm(
                "Are you sure you want to delete this record?"
              );
              if (confirmDelete) {
                await handleDelete(props.row.original.id);
              }
            }}
            data-tooltip-content="Delete"
            data-tooltip-id="deleteTooltip"
          >
            <ReactTooltip id="deleteTooltip" />
            <TrashIcon className="h-4 w-4 text-red-500" />
          </div>
        ),
      },
      ...[
        "reasoncode",
        "description",
        "reasontypename",
        "rolename",
        "errorcategory",
        "errorsubcategory",
        "doctypes",
      ].map((key) =>
        columnHelper.accessor(key, {
          header: createHeader(key) as any,
          cell: (props: any) => {
            const value = props.getValue();
            // Render boolean values as "Yes" or "No"
            if (typeof value === "boolean") {
              return <div>{value ? "Yes" : "No"}</div>;
            }
            return <div>{value}</div>;
          },
        })
      ),
    ];
  }, [
    selectedRowIds,
    data,
    columnHeaders,
    searchInputs,
    handleInputChange,
    handleDelete,
    columnHelper,
  ]);

  const tableObj = useReactTable({
    columns: columns as any,
    data: paginatedData,
    enableRowSelection: true,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchData = async () => {
    const { data, status } = await getReasonCodeDetails();
    console.log(data, status);
    if (status) {
      onDataUpdate((data as any).data);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.length) return;

    const file = event.target.files[0];
    const fileName = file.name;
    const fileType = file.type;

    // Define valid extensions and MIME types for Excel files
    const validExcelExtensions = [".xls", ".xlsx", ".csv"];
    const validExcelMIMETypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv", // Added for CSV MIME type
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv", // Added for CSV MIME type
    ];

    // Check file extension and MIME type
    if (
      !isValidFile(
        fileName,
        fileType,
        validExcelExtensions,
        validExcelMIMETypes
      )
    ) {
      toast.error(
        "Files with extension .xls, .xslx, and .csv are allowed to browse.",
        {
          theme: "colored",
        }
      );
      return;
    }
    try {
      const base64Content = await readFileAsBase64(file);
      setShowSuccessModalReload(true);
      setSuccessMessage("Uploading.....");

      const { status, data: response, error} = await uploadreasoncodexl(
        fileName,
        base64Content
      );

      if (status) {
        console.log((response as any).data);
        setSuccessMessage((response as any).data);
        fetchData();
      } else {
        throw new Error(error as string);
      }
    } catch (error) {
      setSuccessMessage("Upload failed");
      console.error("File upload failed:", error);
      toast.error(
          "File upload failed:" + error,
        {
          theme: "colored",
        }
      );
    } finally {
      // Clear the file input value
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setTimeout(() => {
        setShowSuccessModalReload(false);
      }, 3000);
    }
  };

  const isValidFile = (
    fileName: string,
    fileType: string,
    validExtensions: string[],
    validMIMETypes: string[]
  ): boolean => {
    const fileExtension = fileName
      .slice(fileName.lastIndexOf("."))
      .toLowerCase();
    const isValidExtension = validExtensions.includes(fileExtension);
    const isValidMIMEType = validMIMETypes.includes(fileType);
    return isValidExtension && isValidMIMEType;
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] || result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(0); // Reset to first page
  };

  return (
    <>
      <div className="w-full">
        <div className="flex justify-between mb-4">
          <div>
            <label>
              Rows per page:
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="ml-2 border border-gray-300 rounded"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={sortedData.length}>All</option>
              </select>
            </label>
          </div>
          <div>
            <button
              onClick={handleOpenDownloadModal}
              className="bg-orange-400 text-white py-2 px-4 rounded mr-1"
            >
              Download Template
            </button>
            <label className="bg-orange-400 text-white py-2 px-4 rounded mr-1">
              Upload file
              <input
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                ref={fileInputRef}
              />
            </label>
            <button
              onClick={handleExportToExcel}
              className="bg-orange-400 text-white py-2 px-4 rounded mr-1"
            >
              Export to Excel
            </button>

            <button
              onClick={() => handleEditClick(0)}
              className="bg-orange-400 text-white py-2 px-4 rounded mr-1"
            >
              Add
            </button>
            <button
              // onClick={DeleteAll}
              onClick={async () => {
              const confirmDelete = window.confirm(
                "Are you sure you want to delete this record?"
              );
              if (confirmDelete) {
                await DeleteAll();
              }
            }}
              className="bg-orange-400 text-white py-2 px-4 rounded"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="overflow-auto max-w-full h-[600px]">
          <table className="table-auto w-full border-collapse w-full mb-2">
            <thead className="bg-neutral-300 sticky top-0">
              {tableObj.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      className="border border-neutral-300 capitalize"
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableObj.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border border-neutral-300">
                  {row.getVisibleCells().map((cell) => (
                    <td className="p-2 m-2" key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center mt-4">
            <div className="p-4">
              Showing {paginatedData.length} of {filteredData.length} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                disabled={currentPage === 0}
                className="border border-gray-300 px-4 py-2 rounded mr-2"
              >
                Previous
              </button>
              <span>
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() =>
                  handlePageChange(Math.min(currentPage + 1, totalPages - 1))
                }
                disabled={currentPage >= totalPages - 1}
                className="border border-gray-300 px-4 py-2 rounded ml-2"
              >
                Next
              </button>
            </div>
          </div>
        {isEditModalOpen && EditedUser !== null && (
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setEditedUser(EditedUser);
            }}
            id={EditedUser}
          />
        )}

        <DownloadModal
          isOpen={isDownloadModalOpen}
          onClose={handleCloseDownloadModal}
        />

        {showSuccessModalReload && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-4 rounded shadow-lg">
              <table className="min-w-full mt-4 border-collapse border border-gray-200">
                <tbody>
                  {SuccessMessage.split("/").map((part, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 p-2">
                        {part.trim()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {showSuccessModalReload && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-4 rounded shadow-lg">
              <table className="min-w-full mt-4 border-collapse border border-gray-200">
                <tbody>
                  {SuccessMessage.split("/").map((part, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 p-2">
                        {part.trim()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default ReasonCodeTable;
